import { Dict, RequestOptions } from 'mixpanel-browser';

export const SearchSuggestionSource = {
    RECENT_HISTORY: 'recent_history',
    AUTOCOMPLETE: 'autocomplete',
} as const;
export type SearchSuggestionSourceValue = (typeof SearchSuggestionSource)[keyof typeof SearchSuggestionSource];

// Distinct domain from SearchSuggestionSource: this describes how the query reached
// the search bar (entry mode), while SearchSuggestionSource describes which dropdown
// list a suggestion came from. The shared 'recent_history'/'autocomplete' literals are
// intentional — keep these enums separate.
export const SearchEntryMethod = {
    TYPED: 'typed',
    RECENT_HISTORY: 'recent_history',
    AUTOCOMPLETE: 'autocomplete',
} as const;
export type SearchEntryMethodValue = (typeof SearchEntryMethod)[keyof typeof SearchEntryMethod];

export const SearchSelectionMethod = {
    MOUSE: 'mouse',
    KEYBOARD_ENTER: 'keyboard_enter',
    TYPED_ONLY: 'typed_only',
} as const;
export type SearchSelectionMethodValue = (typeof SearchSelectionMethod)[keyof typeof SearchSelectionMethod];

export const SearchPaginationMethod = {
    PAGE_NUMBER: 'page_number',
    NEXT: 'next',
    PREV: 'prev',
} as const;
export type SearchPaginationMethodValue = (typeof SearchPaginationMethod)[keyof typeof SearchPaginationMethod];

export const SearchEvents = {
    PAGE_VIEWED: 'SearchPageViewed',
    SUBMITTED: 'SearchSubmitted',
    RESULT_CLICKED: 'SearchResultClicked',
    RESULT_IMPRESSED: 'SearchResultImpressed',
    SORT_CHANGED: 'SearchSortChanged',
    PAGINATION_CLICKED: 'SearchPaginationClicked',
    SUGGESTION_CLICKED: 'SearchSuggestionClicked',
    BAR_CLEARED: 'SearchBarCleared',
} as const;
export type SearchEventName = (typeof SearchEvents)[keyof typeof SearchEvents];

interface SearchSessionProperties {
    /** Identifier of the ranking class that served this request (`self.get_search().ALGORITHM_VERSION`). */
    algorithm_version: string;
    query: string;
    search_session_id: number | null;
}

interface SuggestionsSnapshot {
    suggestions_visible_count: number;
    suggestions_count_recent: number;
    suggestions_count_autocomplete: number;
    had_autocomplete_results: boolean;
}

interface SearchResultProperties extends SearchSessionProperties {
    content_type: string;
    object_id: number;
    title: string;
    position_on_page: number;
    page_number: number;
    total_pages: number;
    results_on_page: number;
    seconds_since_page_load: number;
    channel_id: number | null;
    channel_name: string | null;
    video_length_seconds: number | null;
    video_age_days: number | null;
    views_count: number | null;
    likes_count: number | null;
    contributors_count: number | null;
}

interface SearchEventPropertiesMap {
    [SearchEvents.PAGE_VIEWED]: SearchSessionProperties & {
        query_normalized: string;
        query_length: number;
        sort: string;
        page_number: number;
        total_pages: number;
        is_first_page: boolean;
        is_last_page: boolean;
        results_total: number;
        results_returned: number;
        had_zero_results: boolean;
        has_filters: boolean;
        author_filter: string;
        condition_filter: string;
        drug_filter: string;
    };
    [SearchEvents.SUBMITTED]: SuggestionsSnapshot & {
        query: string;
        query_length: number;
        entry_method: SearchEntryMethodValue;
        selection_method: SearchSelectionMethodValue;
        suggestion_position: number | null;
        time_since_focus_ms: number | null;
        keystrokes_before_submit: number;
    };
    [SearchEvents.RESULT_CLICKED]: SearchResultProperties;
    [SearchEvents.RESULT_IMPRESSED]: SearchResultProperties;
    [SearchEvents.SORT_CHANGED]: SearchSessionProperties & {
        from_sort: string;
        to_sort: string;
        page_number: number;
        total_pages: number;
        results_total: number;
    };
    [SearchEvents.PAGINATION_CLICKED]: SearchSessionProperties & {
        from_page: number;
        to_page: number;
        total_pages: number;
        method: SearchPaginationMethodValue;
        results_total: number;
    };
    [SearchEvents.SUGGESTION_CLICKED]: SuggestionsSnapshot & {
        suggestion_text: string;
        suggestion_source: SearchSuggestionSourceValue;
        suggestion_position: number;
        typed_text: string;
        typed_text_length: number;
        time_since_focus_ms: number | null;
        selection_method: SearchSelectionMethodValue;
    };
    [SearchEvents.BAR_CLEARED]: {
        cleared_by: 'clear_button';
        had_dropdown_open: boolean;
        query_before_clear: string;
        was_on_search_page: boolean;
    };
}

type EventProperties<E extends SearchEventName> = E extends keyof SearchEventPropertiesMap
    ? SearchEventPropertiesMap[E] & Dict
    : Dict;

/**
 * Events that precede navigation must be sent via navigator.sendBeacon, otherwise
 * the in-flight XHR is cancelled the moment the browser starts unloading the page
 * and the event is lost. `send_immediately: true` alone is not enough — it bypasses
 * batching but still uses the configured `api_transport` (XHR by default).
 * Explicitly setting `transport: 'sendBeacon'` is the documented pattern (see
 * mixpanel-core.js JSDoc near `mixpanel.track`).
 */
const EVENTS_NEEDING_BEACON: ReadonlySet<SearchEventName> = new Set([
    SearchEvents.SUBMITTED,
    SearchEvents.RESULT_CLICKED,
    SearchEvents.SORT_CHANGED,
    SearchEvents.PAGINATION_CLICKED,
    SearchEvents.SUGGESTION_CLICKED,
]);

export const trackSearchEvent = <E extends SearchEventName>(eventName: E, properties: EventProperties<E>) => {
    const options: RequestOptions | undefined = EVENTS_NEEDING_BEACON.has(eventName)
        ? { transport: 'sendBeacon', send_immediately: true }
        : undefined;
    window.vumedi?.mixpanel?.track(eventName, properties, options);
};
